export class StreamProtocolError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "StreamProtocolError";
    this.code = code;
  }
}

function parseBlock(block) {
  let event = "message";
  const data = [];
  for (const line of block.split(/\r?\n/)) {
    if (!line || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon < 0 ? line : line.slice(0, colon);
    const value = colon < 0 ? "" : line.slice(colon + 1).replace(/^ /, "");
    if (field === "event") event = value;
    if (field === "data") data.push(value);
  }
  if (!data.length) return null;
  try { return { event, data: JSON.parse(data.join("\n")) }; }
  catch { throw new StreamProtocolError("MALFORMED_EVENT", "Ask Mohamad returned an unreadable stream event."); }
}

export function createSseParser(onEvent) {
  let buffer = "";
  return {
    push(text) {
      buffer += text.replace(/\r\n/g, "\n");
      let boundary;
      while ((boundary = buffer.indexOf("\n\n")) >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseBlock(block);
        if (parsed) onEvent(parsed);
      }
    },
    finish() {
      if (buffer.trim()) throw new StreamProtocolError("INCOMPLETE_EVENT", "Ask Mohamad's response ended unexpectedly.");
    },
  };
}

export async function consumeAskStream(response, handlers = {}, signal) {
  if (!response.body) throw new StreamProtocolError("HTTP_ERROR", "Ask Mohamad is temporarily unavailable. Please try again.");
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let terminal = null;
  const parser = createSseParser(({ event, data }) => {
    if (terminal || signal?.aborted) return;
    if (event === "done" || event === "error") terminal = event;
    if (event === "error" && data.retry_after_seconds === undefined) {
      const value = response.headers?.get?.("retry-after");
      if (typeof value === "string" && /^\d+$/.test(value)) data.retry_after_seconds = Number(value);
    }
    if (["meta", "delta", "sources", "done", "error"].includes(event)) handlers[event]?.(data);
  });
  const cancelReader = () => { const cancellation = reader.cancel?.(); cancellation?.catch?.(() => {}); };
  signal?.addEventListener("abort", cancelReader, { once: true });
  try {
    while (true) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const { done, value } = await reader.read();
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.finish();
    if (!terminal) throw new StreamProtocolError("MISSING_TERMINAL", "Ask Mohamad's response ended unexpectedly.");
    return terminal;
  } finally {
    signal?.removeEventListener("abort", cancelReader);
    reader.releaseLock?.();
  }
}

export async function askMohamad({ message, history = [], signal, handlers = {}, fetchImpl = fetch }) {
  const response = await fetchImpl("/api/portfolio/ask", { method: "POST", headers: { Accept: "text/event-stream", "Content-Type": "application/json" }, body: JSON.stringify({ message, history }), signal });
  return consumeAskStream(response, handlers, signal);
}
